import './DataTable.css'

import type { ReactNode } from 'react'

import { cx } from '../utilities/cx'

export type DataTableColumn<T> = {
  id: string
  header: string
  width?: string
  render: (row: T) => ReactNode
}

export type DataTableProps<T> = {
  className?: string
  columns: DataTableColumn<T>[]
  rows: T[]
  getRowKey: (row: T) => string
  onRowClick?: (row: T) => void
  emptyMessage?: string
}

export const DataTable = <T,>(props: DataTableProps<T>) => {
  const { className, columns, rows, getRowKey, onRowClick, emptyMessage = 'No results' } = props

  if (rows.length === 0) {
    return (
      <div className={cx('data-table', 'data-table--empty', className)}>
        <p className="data-table__empty">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={cx('data-table', className)}>
      <table className="data-table__table">
        <thead className="data-table__head">
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                className="data-table__header"
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="data-table__body">
          {rows.map((row) => (
            <tr
              key={getRowKey(row)}
              className={cx('data-table__row', {
                'data-table__row--clickable': !!onRowClick,
              })}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((column) => (
                <td key={column.id} className="data-table__cell">
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
